/**
 * عروضي — "save with a template": render each CHANGED design to an exact-size
 * PNG, upload it through the same chain the media page uses (uploadToGcs, then
 * POST /media/create) and hand back media ids in design order — that order is
 * what the engine rotates. Dependencies are injected so the whole flow is unit
 * tested without a browser.
 */
import { SHAPE_SIZE } from './constants';
import { wifiPayload } from './qrTarget';

export const PREVIEW_QR_URL = (host) => `${host}/q/abcdefg`; // same length as a real code: identical matrix size, not scannable

/** What the QR encodes + the readable caption. null when the design has no QR. */
export function qrContent(shape, qr, linkInfo, previewHost) {
  if (shape !== 'lshape' || !qr || qr.type === 'none') return null; // QR lives on the L-shape strip only
  if (qr.type === 'wifi') {
    return { text: wifiPayload(qr.ssid, qr.wifiPass), display: qr.ssid ? `Wi-Fi: ${qr.ssid}` : '' };
  }
  if (linkInfo?.short_url) return { text: linkInfo.short_url, display: linkInfo.display || qr.display || '' };
  return { text: PREVIEW_QR_URL(previewHost), display: qr.display || '' };
}

const fileKey = (companyId, ext = 'png') => {
  const rand = Array.from({ length: 24 }, () => 'abcdefghijklmnopqrstuvwxyz0123456789'[Math.floor(Math.random() * 36)]).join('');
  return `${companyId}/root/${rand}.${ext}`;
};

/**
 * @param {object} a
 *  shape, tpl (template state), venue {name, initial, logoUrl}, qr {text, display}|null,
 *  forceAll: re-render every design (shared field changed), companyId
 * @param {object} deps {loadFonts, loadImage, renderDesign, canvasToBlob, uploadToGcs, createMediaRecord, onProgress}
 * @returns {Promise<{mediaIds: string[], items: object[]}>} items carry the new mediaId / cleared dirty flag
 */
export async function renderAndUploadDesigns(a, deps) {
  const { shape, tpl, venue, qr, forceAll, companyId } = a;
  if (!SHAPE_SIZE[shape]) throw new Error(`unsupported shape ${shape}`);
  await deps.loadFonts([venue.name, tpl.venueName, ...tpl.items.flatMap((i) => [i.title, i.line, i.price]), qr?.display || '']);
  // A logo that exists but cannot be loaded must STOP the save: silently drawing without it put a different (or no) logo on the TV.
  let logo = null;
  if (venue.logoUrl) {
    try { logo = await deps.loadImage(venue.logoUrl); } catch (e) { throw new Error('logo_load_failed'); }
  }
  const out = [];
  for (let i = 0; i < tpl.items.length; i += 1) {
    const it = tpl.items[i];
    if (!forceAll && !it.dirty && it.mediaId) { out.push(it); continue; }
    const photo = it.photo?.url ? await deps.loadImage(it.photo.url) : null;
    const canvas = deps.renderDesign({
      shape, palette: it.palette, currency: tpl.currency,
      item: { title: it.title.trim(), line: it.line.trim(), price: it.price.trim(), palette: it.palette },
      venue: { name: venue.name, initial: venue.initial, logo, venueName: tpl.venueName || venue.name }, brand: tpl.brand || undefined, photo, qr,
    });
    const blob = await deps.canvasToBlob(canvas);
    const file = new File([blob], `${(it.title || 'promo').slice(0, 20)}.png`, { type: 'image/png' });
    const url = await deps.uploadToGcs(file, fileKey(companyId), (pct) => deps.onProgress?.(i, tpl.items.length, pct));
    const mediaId = await deps.createMediaRecord({ name: file.name, size: file.size, path: url });
    if (!mediaId) throw new Error('media record was not created');
    out.push({ ...it, mediaId, image: url, dirty: false });
  }
  return { mediaIds: out.map((x) => x.mediaId), items: out };
}
